/**
 * Символы раздела «Мебель» для развёртки стен: предмет спереди и, где это другой рисунок, сбоку.
 *
 * Развёртка — рабочий чертёж, поэтому рисунок контурный: корпус, фасады, ножки, подушки —
 * без теней и фактуры. Рисунок задан в миллиметрах типового предмета (viewBox), низ — пол;
 * в габарит конкретной модели он растягивается рамкой символа. Сбоку перед предмета смотрит
 * вправо, зеркалит развёртка. У коробок (шкаф, комод, холодильник) вида сбоку нет — сбоку
 * они просто прямоугольник, его рисует сама развёртка. Ключ реестра — id элемента каталога;
 * предмета нет в реестре — развёртка покажет его прямоугольником с подписью.
 * Здесь — спальня, гостиная, хранение, кухня и столы; техника и свет — в `elevationTech`,
 * спорт, детское и прочее — в `elevationLeisure`.
 */
import { UIKit, type ElevationSymbols, type SymbolComponent, type SymbolProps } from "@/shared/ui";
import { Fronts, Legs, handle, line, thin } from "../../elevationParts";
import { KITCHEN_HOB_AT, KITCHEN_SINK_AT } from "../3d/KitchenModels";
import { FURNITURE_LEISURE_ELEVATIONS } from "./elevationLeisure";
import { FURNITURE_TECH_ELEVATIONS } from "./elevationTech";

/* ——— Кровати ——— */

/** Кровать спереди, со стороны ног: изголовье сзади, над матрасом подушки, рама на ножках */
const BedFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1700 1000" {...props}>
		<rect x="20" y="10" width="1660" height="700" rx="40" {...line} />
		<rect x="190" y="300" width="580" height="150" rx="70" {...thin} />
		<rect x="930" y="300" width="580" height="150" rx="70" {...thin} />
		<rect x="60" y="420" width="1580" height="200" rx="50" {...line} />
		<rect x="20" y="610" width="1660" height="270" rx="16" {...line} />
		<Legs left={70} right={1630} top={880} floor={1000} />
	</UIKit.SymbolFrame>
);

/** Кровать сбоку: изголовье слева до пола, подушка у изголовья, матрас на раме */
const BedSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 2150 1000" {...props}>
		<rect x="10" y="10" width="90" height="990" rx="20" {...line} />
		<rect x="120" y="300" width="480" height="150" rx="70" {...thin} />
		<rect x="100" y="420" width="2020" height="200" rx="50" {...line} />
		<rect x="100" y="610" width="2040" height="270" rx="16" {...line} />
		<Legs left={120} right={2120} top={880} floor={1000} />
	</UIKit.SymbolFrame>
);

/* ——— Диваны и кресла ——— */

/** Типовой габарит мягкой мебели, мм, и сколько подушек в сиденье */
interface SeatingSize {
	viewWidth: number;
	viewHeight: number;
	seats?: number;
}

/**
 * Мягкая мебель спереди: спинка, подлокотники, подушки сиденья на основании.
 * Обычный компонент, а не фабрика: React Compiler выносит колбэки из фабрик наружу
 * и теряет замкнутые в них переменные.
 */
const SeatingFrontShape = ({
	viewWidth: width,
	viewHeight: height,
	seats = 1,
	...props
}: SymbolProps & SeatingSize) => {
	const arm = Math.min(230, width * 0.16);
	const seatTop = height * 0.55;
	const baseTop = height * 0.75;
	const baseBottom = height - 70;
	const cushion = (width - arm * 2) / seats;
	return (
		<UIKit.SymbolFrame viewBox={`0 0 ${width} ${height}`} {...props}>
			<rect x={arm - 30} y="20" width={width - arm * 2 + 60} height={seatTop} rx="60" {...line} />
			<rect x="10" y={baseTop} width={width - 20} height={baseBottom - baseTop} rx="20" {...line} />
			{Array.from({ length: seats }, (_, i) => (
				<rect
					key={i}
					x={arm + i * cushion}
					y={seatTop}
					width={cushion}
					height={baseTop - seatTop + 20}
					rx="40"
					{...thin}
				/>
			))}
			<rect
				x="10"
				y={height * 0.3}
				width={arm}
				height={baseBottom - height * 0.3}
				rx="60"
				{...line}
			/>
			<rect
				x={width - 10 - arm}
				y={height * 0.3}
				width={arm}
				height={baseBottom - height * 0.3}
				rx="60"
				{...line}
			/>
			<Legs left={60} right={width - 60} top={baseBottom} floor={height} width={60} />
		</UIKit.SymbolFrame>
	);
};

/** Мягкая мебель сбоку: спинка слева выше всего, перед ней подлокотник во всю глубину */
const SeatingSideShape = ({
	viewWidth: depth,
	viewHeight: height,
	seats: _seats,
	...props
}: SymbolProps & SeatingSize) => {
	const baseBottom = height - 70;
	return (
		<UIKit.SymbolFrame viewBox={`0 0 ${depth} ${height}`} {...props}>
			<path
				d={`M30 40Q30 20 60 20H${depth * 0.3}Q${depth * 0.34} 20 ${depth * 0.34} 60L${depth * 0.34} ${baseBottom}H30Z`}
				{...line}
			/>
			<rect
				x="20"
				y={height * 0.3}
				width={depth - 40}
				height={baseBottom - height * 0.3}
				rx="60"
				{...line}
			/>
			<Legs left={50} right={depth - 50} top={baseBottom} floor={height} width={60} />
		</UIKit.SymbolFrame>
	);
};

const SofaFront: SymbolComponent = (props) => (
	<SeatingFrontShape {...props} viewWidth={2200} viewHeight={850} seats={2} />
);
const SofaSide: SymbolComponent = (props) => (
	<SeatingSideShape {...props} viewWidth={900} viewHeight={850} />
);
const ArmchairFront: SymbolComponent = (props) => (
	<SeatingFrontShape {...props} viewWidth={860} viewHeight={950} />
);
const ArmchairSide: SymbolComponent = (props) => (
	<SeatingSideShape {...props} viewWidth={900} viewHeight={950} />
);

/** Стул спереди: спинка, сиденье, ножки */
const ChairFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 450 880" {...props}>
		<rect x="50" y="10" width="350" height="390" rx="30" {...line} />
		<rect x="20" y="430" width="410" height="60" rx="14" {...line} />
		<Legs left={35} right={415} top={490} floor={880} width={40} />
	</UIKit.SymbolFrame>
);

/** Стул сбоку: задняя ножка переходит в спинку, сиденье, передняя ножка справа */
const ChairSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 500 880" {...props}>
		<path d="M40 10H95L80 880H40Z" {...line} />
		<rect x="40" y="430" width="440" height="60" rx="14" {...line} />
		<rect x="420" y="490" width="45" height="390" {...thin} />
	</UIKit.SymbolFrame>
);

/** Офисное кресло спереди: спинка, сиденье, газлифт и крестовина на колёсиках */
const OfficeChairFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 620 1100" {...props}>
		<rect x="110" y="10" width="400" height="540" rx="90" {...line} />
		<rect x="60" y="560" width="500" height="100" rx="40" {...line} />
		<rect x="290" y="660" width="40" height="290" {...thin} />
		<path d="M310 950L50 1010M310 950L570 1010M310 950V1020" {...line} />
		<circle cx="50" cy="1050" r="38" {...thin} />
		<circle cx="570" cy="1050" r="38" {...thin} />
		<circle cx="310" cy="1055" r="38" {...thin} />
	</UIKit.SymbolFrame>
);

/** Офисное кресло сбоку: наклонная спинка слева, сиденье, газлифт и крестовина */
const OfficeChairSide: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 620 1100" {...props}>
		<path d="M40 30Q60 10 120 20L170 560H80Z" {...line} />
		<rect x="60" y="560" width="520" height="100" rx="40" {...line} />
		<rect x="290" y="660" width="40" height="290" {...thin} />
		<path d="M310 950L50 1010M310 950L570 1010" {...line} />
		<circle cx="50" cy="1050" r="38" {...thin} />
		<circle cx="570" cy="1050" r="38" {...thin} />
	</UIKit.SymbolFrame>
);

/* ——— Хранение ——— */

/** Шкаф распашной: корпус, две двери с ручками у стыка, цоколь */
const WardrobeFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 2000" {...props}>
		<rect x="10" y="10" width="1180" height="1930" {...line} />
		<Fronts x={10} y={10} width={1180} height={1930} count={2} />
		<rect x="40" y="1940" width="1120" height="60" {...thin} />
	</UIKit.SymbolFrame>
);

/** Шкаф-купе: три раздвижные двери-зеркала внахлёст, цоколь */
const SlidingWardrobeFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 2000 2000" {...props}>
		<rect x="10" y="10" width="1980" height="1930" {...line} />
		{[0, 1, 2].map((i) => {
			const x = 30 + i * 646;
			return (
				<g key={i}>
					<rect x={x} y="40" width="646" height="1870" {...thin} />
					<path
						d={`M${x + 180} 500L${x + 380} 300M${x + 260} 560L${x + 460} 360`}
						{...thin}
						strokeWidth="5"
					/>
				</g>
			);
		})}
		<rect x="40" y="1940" width="1920" height="60" {...thin} />
	</UIKit.SymbolFrame>
);

/** Комод: четыре ящика с ручками, ножки */
const DresserFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 930 1000" {...props}>
		<rect x="10" y="10" width="910" height="900" {...line} />
		<Fronts x={10} y={10} width={910} height={900} count={1} rows={4} drawers />
		<Legs left={40} right={890} top={910} floor={1000} width={60} />
	</UIKit.SymbolFrame>
);

/** Прикроватная тумба: два ящика, ножки */
const NightstandFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 500 510" {...props}>
		<rect x="10" y="10" width="480" height="420" {...line} />
		<Fronts x={10} y={10} width={480} height={420} count={1} rows={2} drawers />
		<Legs left={30} right={470} top={430} floor={510} width={45} />
	</UIKit.SymbolFrame>
);

/** Тумба под телевизор: длинный низкий корпус с четырьмя дверцами на ножках */
const TvStandFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1600 500" {...props}>
		<rect x="10" y="10" width="1580" height="400" {...line} />
		<Fronts x={10} y={10} width={1580} height={400} count={4} />
		<Legs left={50} right={1550} top={410} floor={500} width={50} />
	</UIKit.SymbolFrame>
);

/** Стеллаж: открытые полки и несколько книг на них */
const BookshelfFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 1700" {...props}>
		<rect x="10" y="10" width="980" height="1680" {...line} />
		{[340, 680, 1020, 1360].map((y) => (
			<path key={y} d={`M10 ${y}H990`} {...line} />
		))}
		{[
			[60, 120],
			[120, 160],
			[175, 110],
			[600, 140],
			[650, 100],
			[120, 1200],
			[175, 1150],
			[230, 1180],
			[520, 830],
			[575, 860],
		].map(([x, top]) => {
			const shelf = [340, 680, 1020, 1360, 1690].find((s) => s > top) ?? 340;
			return <rect key={`${x}-${top}`} x={x} y={top} width="45" height={shelf - top} {...thin} />;
		})}
	</UIKit.SymbolFrame>
);

/** Стеллаж из ячеек: сетка 4 × 3 */
const CubeShelfFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 1470" {...props}>
		<rect x="10" y="10" width="1180" height="1450" {...line} />
		{[305, 600, 895].map((x) => (
			<path key={x} d={`M${x} 10V1460`} {...line} />
		))}
		{[493, 976].map((y) => (
			<path key={y} d={`M10 ${y}H1190`} {...line} />
		))}
	</UIKit.SymbolFrame>
);

/** Витрина: две стеклянные двери с полками за ними, цоколь */
const DisplayCabinetFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 1900" {...props}>
		<rect x="10" y="10" width="1180" height="1820" {...line} />
		<Fronts x={10} y={10} width={1180} height={1820} count={2} />
		{[460, 910, 1360].map((y) => (
			<path key={y} d={`M60 ${y}H1140`} {...thin} strokeDasharray="30 20" />
		))}
		<rect x="40" y="1830" width="1120" height="70" {...thin} />
	</UIKit.SymbolFrame>
);

/* ——— Кухня ——— */

const KITCHEN_W = 2400;
const KITCHEN_H = 2200;
/** Высоты кухни от пола, мм — те же, что у её 3D-модели */
const WORKTOP = 900;
const WORKTOP_THICK = 40;
const PLINTH = 100;
const UPPER_FROM = 1450;
const y = (fromFloor: number) => KITCHEN_H - fromFloor;
const SINK_X = KITCHEN_W * (0.5 + KITCHEN_SINK_AT);
const HOB_X = KITCHEN_W * (0.5 + KITCHEN_HOB_AT);

/** Кухонный гарнитур: верхние шкафы, столешница с мойкой и варочной панелью, духовка, цоколь */
const KitchenSetFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox={`0 0 ${KITCHEN_W} ${KITCHEN_H}`} {...props}>
		{/* Верхние шкафы */}
		<rect x="10" y="10" width={KITCHEN_W - 20} height={y(UPPER_FROM) - 10} {...line} />
		<Fronts x={10} y={10} width={KITCHEN_W - 20} height={y(UPPER_FROM) - 10} count={4} />
		{/* Смеситель над мойкой */}
		<path
			d={`M${SINK_X} ${y(WORKTOP)}V${y(WORKTOP) - 230}H${SINK_X + 110}V${y(WORKTOP) - 180}`}
			{...line}
		/>
		{/* Варочная панель — тонкая пластина на столешнице */}
		<rect x={HOB_X - 290} y={y(WORKTOP) - 20} width="580" height="20" {...thin} />
		{/* Столешница */}
		<rect x="10" y={y(WORKTOP)} width={KITCHEN_W - 20} height={WORKTOP_THICK} {...line} />
		{/* Нижние фасады; под варочной панелью — духовка */}
		<rect
			x="10"
			y={y(WORKTOP - WORKTOP_THICK)}
			width={KITCHEN_W - 20}
			height={WORKTOP - WORKTOP_THICK - PLINTH}
			{...line}
		/>
		<Fronts
			x={10}
			y={y(WORKTOP - WORKTOP_THICK)}
			width={KITCHEN_W - 20}
			height={WORKTOP - WORKTOP_THICK - PLINTH}
			count={4}
		/>
		<rect
			x={HOB_X - 270}
			y={y(WORKTOP - WORKTOP_THICK) + 150}
			width="540"
			height="460"
			rx="20"
			{...line}
		/>
		<rect
			x={HOB_X - 210}
			y={y(WORKTOP - WORKTOP_THICK) + 260}
			width="420"
			height="290"
			rx="20"
			{...thin}
		/>
		<rect x="40" y={y(PLINTH)} width={KITCHEN_W - 80} height={PLINTH} {...thin} />
	</UIKit.SymbolFrame>
);

/** Холодильник двухкамерный: верхняя камера, морозилка снизу, ручки справа */
const FridgeFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 600 1850" {...props}>
		<rect x="10" y="10" width="580" height="1820" rx="20" {...line} />
		<path d="M10 1180H590" {...line} />
		<path d="M530 250V650M530 1260V1500" {...handle} />
	</UIKit.SymbolFrame>
);

/** Холодильник Side-by-Side: две высокие двери, ручки у стыка */
const SideBySideFridgeFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 900 1800" {...props}>
		<rect x="10" y="10" width="880" height="1780" rx="20" {...line} />
		<path d="M450 10V1790" {...line} />
		<path d="M400 600V1100M500 600V1100" {...handle} />
		<rect x="120" y="350" width="220" height="300" rx="14" {...thin} />
	</UIKit.SymbolFrame>
);

/** Плита: панель с ручками-регуляторами, дверца духовки со стеклом */
const StoveFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 650 850" {...props}>
		<rect x="10" y="10" width="630" height="830" rx="10" {...line} />
		<path d="M10 150H640" {...line} />
		{[110, 220, 430, 540].map((x) => (
			<circle key={x} cx={x} cy="80" r="30" {...thin} />
		))}
		<rect x="60" y="210" width="530" height="520" rx="16" {...line} />
		<rect x="120" y="320" width="410" height="300" rx="16" {...thin} />
		<path d="M150 250H500" {...handle} />
	</UIKit.SymbolFrame>
);

/** Посудомоечная машина: полоса панели управления сверху и глухая дверца */
const DishwasherFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 600 850" {...props}>
		<rect x="10" y="10" width="580" height="830" rx="10" {...line} />
		<path d="M10 130H590" {...line} />
		<rect x="380" y="50" width="160" height="40" rx="10" {...thin} />
		<path d="M200 200H400" {...handle} />
	</UIKit.SymbolFrame>
);

/* ——— Столы ——— */

/** Обеденный стол: столешница, царга, четыре ножки (видно две) — сбоку такой же */
const DiningTableFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 750" {...props}>
		<rect x="10" y="10" width="1180" height="50" {...line} />
		<rect x="60" y="60" width="1080" height="70" {...thin} />
		<Legs left={60} right={1140} top={130} floor={750} width={60} />
	</UIKit.SymbolFrame>
);

/** Круглый стол: столешница на центральной опоре с широким основанием */
const RoundTableFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 750" {...props}>
		<rect x="10" y="10" width="1180" height="50" {...line} />
		<rect x="560" y="60" width="80" height="640" {...thin} />
		<path d="M330 740Q600 660 870 740Z" {...line} />
	</UIKit.SymbolFrame>
);

/** Журнальный столик: столешница, нижняя полка, ножки */
const CoffeeTableFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1000 380" {...props}>
		<rect x="10" y="10" width="980" height="45" {...line} />
		<Legs left={40} right={960} top={55} floor={380} width={50} />
		<path d="M90 280H910" {...line} />
	</UIKit.SymbolFrame>
);

/** Компьютерный стол: столешница, тумба с тремя ящиками слева, боковина справа */
const DeskFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1400 750" {...props}>
		<rect x="10" y="10" width="1380" height="40" {...line} />
		<rect x="40" y="50" width="440" height="700" {...line} />
		<Fronts x={40} y={50} width={440} height={680} count={1} rows={3} drawers />
		<rect x="1310" y="50" width="50" height="700" {...line} />
	</UIKit.SymbolFrame>
);

/** Письменный стол: столешница на двух боковинах, сбоку — такая же рама */
const PlainDeskFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1100 750" {...props}>
		<rect x="10" y="10" width="1080" height="40" {...line} />
		<rect x="40" y="50" width="50" height="700" {...line} />
		<rect x="1010" y="50" width="50" height="700" {...line} />
		<path d="M90 160H1010" {...thin} />
	</UIKit.SymbolFrame>
);

/** Банкетка: мягкое сиденье на ножках, сбоку такая же */
const BenchFront: SymbolComponent = (props) => (
	<UIKit.SymbolFrame viewBox="0 0 1200 470" {...props}>
		<rect x="10" y="10" width="1180" height="170" rx="50" {...line} />
		<rect x="30" y="180" width="1140" height="60" {...thin} />
		<Legs left={50} right={1150} top={240} floor={470} width={50} />
	</UIKit.SymbolFrame>
);

const BED: ElevationSymbols = { front: BedFront, side: BedSide };
const SOFA: ElevationSymbols = { front: SofaFront, side: SofaSide };
const ARMCHAIR: ElevationSymbols = { front: ArmchairFront, side: ArmchairSide };
const CHAIR: ElevationSymbols = { front: ChairFront, side: ChairSide };
const OFFICE_CHAIR: ElevationSymbols = { front: OfficeChairFront, side: OfficeChairSide };
const WARDROBE: ElevationSymbols = { front: WardrobeFront };
const DRESSER: ElevationSymbols = { front: DresserFront };
const NIGHTSTAND: ElevationSymbols = { front: NightstandFront };
const TV_STAND: ElevationSymbols = { front: TvStandFront };
const BOOKSHELF: ElevationSymbols = { front: BookshelfFront };
const CUBE_SHELF: ElevationSymbols = { front: CubeShelfFront };
const DINING_TABLE: ElevationSymbols = { front: DiningTableFront, side: DiningTableFront };
const ROUND_TABLE: ElevationSymbols = { front: RoundTableFront, side: RoundTableFront };
const COFFEE_TABLE: ElevationSymbols = { front: CoffeeTableFront, side: CoffeeTableFront };
const PLAIN_DESK: ElevationSymbols = { front: PlainDeskFront, side: PlainDeskFront };

const variants = (
	symbols: ElevationSymbols,
	ids: readonly string[],
): Record<string, ElevationSymbols> => Object.fromEntries(ids.map((id) => [id, symbols]));

const BED_IDS = [
	"fur-140",
	"bed-taupe",
	"bed-oak",
	"bed-single",
	"bed-scandi",
	"bed-wooden",
	"bed-single-wood",
	"bed-single-soft",
	"bed-single-storage",
	"bed-classic",
	"bed-king",
	"bed-luxury-2",
	"bed-luxury-3",
	"bed-luxury-4",
	"bed-pink",
	"bed-tufted",
	"bed-modern",
	"bed-kids-sofa",
	"bed-kids-wood",
] as const;

/** Диваны пятой партии: прямые и угловые на развёртке рисуются одинаково */
const SOFA_IDS = [
	"sofa-beige",
	"sofa-brown",
	"sofa-leather",
	"sofa-classic",
	"sofa-cream",
	"sofa-fabric",
	"sofa-modular",
	"sofa-sectional",
	"sofa-corner-black",
	"sofa-corner-brown",
	"sofa-corner-curved",
	"sofa-corner-leather",
	"sofa-corner-modular",
	"sofa-corner-orange",
	"sofa-corner-sectional",
] as const;

const ARMCHAIR_IDS = [
	"armchair-beige",
	"armchair-classic",
	"armchair-leather",
	"armchair-soft",
	"armchair-wood",
] as const;

const CHAIR_IDS = [
	"chair-modern",
	"chair-wooden",
	"chair-wooden-2",
	"chair-white",
	"chair-green",
	"chair-folding-wood",
] as const;

const OFFICE_CHAIR_IDS = [
	"chair-office-leather",
	"chair-office-dystopian",
	"chair-office-2",
	"chair-office-3",
] as const;

export const FURNITURE_ELEVATION_SYMBOLS: Record<string, ElevationSymbols> = {
	...FURNITURE_TECH_ELEVATIONS,
	...FURNITURE_LEISURE_ELEVATIONS,
	...variants(BED, BED_IDS),
	...variants(SOFA, SOFA_IDS),
	...variants(ARMCHAIR, ARMCHAIR_IDS),
	...variants(CHAIR, CHAIR_IDS),
	...variants(OFFICE_CHAIR, OFFICE_CHAIR_IDS),
	...variants(DRESSER, ["sideboard-grey", "dresser-low", "toy-chest"]),
	"desk-modern": PLAIN_DESK,
	"tv-stand-wood": TV_STAND,
	"nightstand-bedside": NIGHTSTAND,
	"nightstand-table": NIGHTSTAND,
	"bookshelf-antique": BOOKSHELF,
	"bookshelf-low": BOOKSHELF,
	"wardrobe-wood": WARDROBE,
	"bench-ornate": { front: BenchFront, side: BenchFront },
	sofa: SOFA,
	"sofa-accordion": SOFA,
	"sofa-compact": SOFA,
	"sofa-chesterfield": SOFA,
	"sofa-midcentury": SOFA,
	"sofa-scandi": SOFA,
	"sofa-rounded": SOFA,
	"sofa-dark": SOFA,
	"sofa-olive": SOFA,
	"sofa-kids": SOFA,
	armchair: ARMCHAIR,
	"armchair-wingback": ARMCHAIR,
	"armchair-lounge": ARMCHAIR,
	"armchair-rocking": ARMCHAIR,
	chair: CHAIR,
	"chair-soft": CHAIR,
	"chair-wood": CHAIR,
	"chair-plastic": CHAIR,
	"chair-office": OFFICE_CHAIR,
	"chair-gaming": OFFICE_CHAIR,
	"chair-mesh": OFFICE_CHAIR,
	wardrobe: WARDROBE,
	"wardrobe-oak": WARDROBE,
	"wardrobe-shelves": WARDROBE,
	"wardrobe-sliding": { front: SlidingWardrobeFront },
	dresser: DRESSER,
	"dresser-tall": DRESSER,
	"shoe-cabinet": DRESSER,
	sideboard: DRESSER,
	nightstand: NIGHTSTAND,
	"nightstand-white": NIGHTSTAND,
	"tv-stand": TV_STAND,
	"tv-stand-walnut": TV_STAND,
	"tv-stand-white": TV_STAND,
	bookshelf: BOOKSHELF,
	bookcase: BOOKSHELF,
	"shelf-cube": CUBE_SHELF,
	"toy-storage": CUBE_SHELF,
	"display-cabinet": { front: DisplayCabinetFront },
	"kitchen-set": { front: KitchenSetFront },
	fridge: { front: FridgeFront },
	"fridge-side": { front: SideBySideFridgeFront },
	stove: { front: StoveFront },
	dishwasher: { front: DishwasherFront },
	"dining-table": DINING_TABLE,
	"table-white": DINING_TABLE,
	"table-oak": DINING_TABLE,
	"table-glass": DINING_TABLE,
	"table-round": ROUND_TABLE,
	"table-marble": ROUND_TABLE,
	"table-side": ROUND_TABLE,
	"coffee-table": COFFEE_TABLE,
	"console-table": COFFEE_TABLE,
	"desk-computer": { front: DeskFront, side: PlainDeskFront },
	"desk-oak": PLAIN_DESK,
	"desk-standing": PLAIN_DESK,
	"desk-gaming": PLAIN_DESK,
	"desk-kids": PLAIN_DESK,
};
